import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Table } from './components/Table';
import '../../../../../store/js/modules/statspro/stats-leaders/reducer/types';
import { Loader } from '../../../../../../../common/react/components/Loader';
import {
    playerStatsSelector,
    statsTypeSelector,
    isReadyToFetchSelector,
    roundSelector,
    seasonSelector,
    statsLeadersNavSelector,
    statsLeadersBenchmarkingSelector,
    statsLeadersRequestsSelector
} from '../../../../../store/js/modules/statspro/stats-leaders/selectors';
import { FETCH_STATS_LEADERS } from '../../../../../store/js/modules/statspro/stats-leaders/actions';
import {
    composeRowDataForPlayer,
    sortPlayersBySurname,
    composeColumnData,
} from './utils';

export const StatsLeadersTable = () => {
    const dispatch = useDispatch();
    
    const players = useSelector( playerStatsSelector );
    const nav = useSelector( statsLeadersNavSelector );
    const benchmarking = useSelector( statsLeadersBenchmarkingSelector );
    const season = useSelector( seasonSelector );
    const round = useSelector( roundSelector );
    const statsType = useSelector( statsTypeSelector );
    const isReadyToFetch = useSelector( isReadyToFetchSelector );
    const requests = useSelector( statsLeadersRequestsSelector );

    /**
     * 1. Check that a request for this information is not already in progress
     *    before proceeding.
     *
     * @param {object} arg - Object.
     * @param {'standard'|'benchmarking'} arg.type - Type of request.
     */
    const fetchStatsLeaders = ( { type } ) => {
        const reqArgs = {
            id: ( statsType === 'seasons' ? season : round ).providerId,
            type: statsType,
            benchmarking: type === 'benchmarking'
        };

        // [1]
        const existingRequest = requests.find( x => _.isEqual( x, reqArgs ) );
        if ( existingRequest ) { return; }

        dispatch( FETCH_STATS_LEADERS.request( reqArgs ) );
    };

    useEffect( () => {
        if ( !isReadyToFetch ) { return; }

        if ( !players ) {
            fetchStatsLeaders( { type: 'standard' } );
        }

        if ( benchmarking.isActive && !playersHaveBenchmarkingData( players ) ) {
            fetchStatsLeaders( { type: 'benchmarking' } );
        }
    }, [ players, isReadyToFetch, benchmarking ] );

    /**
     * Get column details.
     */
    const columns = useMemo(
        () => composeColumnData( nav.category ),
        [ nav.category ]
    );

    /**
     * Get row data.
     */
    const data = useMemo(
        () => {
            if ( !playersHaveStandardData( players ) ) { return []; }

            const dataColumns = columns.filter( col => {
                return ![
                    'rank',
                    'player',
                    'position',
                    'team'
                ].includes( col.accessor );
            } );
            
            return [
                ...players
                    .map( player => composeRowDataForPlayer( player, dataColumns ) )
                    .sort( sortPlayersBySurname )
            ];
        },
        [ columns, players ]
    );

    if ( !data.length || ( benchmarking.isActive && !playersHaveBenchmarkingData( players ) ) ) {
        return (
            <div style={{
                padding: '3.2rem 1.6rem',
                backgroundColor: '#fff'
            }}>
                <Loader />
            </div>
        );
    }

    return <Table columns={columns} data={data} players={players} />;
};

const playersHaveStandardData = players =>
    players && players.length && players[ 0 ]?.stats?.totals;

const playersHaveBenchmarkingData = players =>
    players && players.length && players[ 0 ]?.stats?.benchmarkedTotals;